import { Dialog } from "primereact/dialog";
import { useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useContext } from "react";
import ContactCard from "../../components/general/contactCard";
import { BRAND_KEY_AFH_HUB } from "../../constants/brandConstants";
import { RouterAddress } from "../../constants/RouterAddress";
import { getCopyrightText } from "../../constants/constants";
import { useAuthExperience } from "../../features/auth/hooks/useAuthExperience";
import { useNavigate } from "react-router-dom";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_FAMILY_MEMBER, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../constants/RegisterConstants";
import {
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  renderSurfaceHeader,
} from "../../components/general/uiSurfaceStyles";

const SUPPORTED_PUBLIC_ROLE_QUERY_TYPES = new Set([
  ROLE_TYPE_PROVIDER,
  ROLE_TYPE_NURSE,
  ROLE_TYPE_CAREGIVER,
  ROLE_TYPE_FAMILY_MEMBER,
]);

const appendRoleQueryParam = (path: string, roleType: string) => {
  const normalizedRoleType = String(roleType || "").trim().toLowerCase();
  if (!SUPPORTED_PUBLIC_ROLE_QUERY_TYPES.has(normalizedRoleType)) {
    return path;
  }

  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}role=${encodeURIComponent(normalizedRoleType)}`;
};

const Footer = () => {
  const { authStore } = useContext(MobXProviderContext);
  const [displayContactDialog, setDisplayContactDialog] = useState(false);
  const isFooterExpanded = true;
  const navigate = useNavigate();
  const userRoleType = authStore.getUserRoleType();
  const authExperience = useAuthExperience();
  const showTutorialVideosLink = authExperience.brandKey === BRAND_KEY_AFH_HUB;

  const footerLinkStyle = {
    color: "#1f4f79",
    textDecoration: "none",
    fontWeight: 500,
    margin: "0.35rem 0",
    display: "block",
  } as const;

  const navigateToPublicPage = (path: string) => {
    navigate(path);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  return (
    <footer
      id="#footer"
      className="footerContainer"
      style={{
        background: "linear-gradient(rgb(255 255 255) 0%, rgb(204, 212, 217) 100%)",
        marginTop: "1em",
      }}
    >
      <div
        className="mx-auto"
        style={{
          maxWidth: "100%",
          padding: "1rem 1.25rem 1.25rem",
        }}
      >
        <div
          className="text-center"
          style={{ color: "#53708a", fontSize: "0.92rem" }}
        >
          <span>{getCopyrightText(authExperience.company.companyName)}</span>
        </div>

        <div
          id="footer-expandable-content"
          style={{
            maxHeight: isFooterExpanded ? "1400px" : "0",
            opacity: isFooterExpanded ? 1 : 0,
            overflow: "hidden",
            transition: "max-height 280ms ease, opacity 220ms ease",
          }}
        >
          <div className="grid justify-content-center align-items-center pt-3 flex flex-wrap flex-column md:flex-row">
            <div className="col flex flex-column align-items-center mb-4">
              <h4 className="m-0" style={{ color: "#0f3d63", fontSize: "1.1rem" }}>{authExperience.brandName}</h4>
              <p style={{ color: "#53708a", fontSize: "0.9rem", marginTop: "0.5rem" }}>
                {authExperience.shell.footerDescription}
              </p>
            </div>

            <div className="col flex flex-column align-items-center mb-4">
              <div className="fw-bold mb-2" style={{ color: "#0f3d63" }}><strong>Product</strong></div>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.ABOUT_US_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>About Us</a>
              <a
                onClick={(e) => {
                  e.preventDefault();
                  setDisplayContactDialog(true);
                }}
                href="#"
                rel="noopener noreferrer"
                style={footerLinkStyle}
              >
                Contact Us
              </a>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(appendRoleQueryParam(RouterAddress.PRICING_PAGE, userRoleType)); }} rel="noopener noreferrer" style={footerLinkStyle}>Pricing</a>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.WHY_USE_HUB_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>Why Use Hub</a>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.RELEASE_NOTES_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>Release Notes</a>
            </div>

            <div className="col flex flex-column align-items-center mb-4">
              <div className="fw-bold mb-2" style={{ color: "#0f3d63" }}><strong>Services</strong></div>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.EMAR_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>eMAR</a>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.ADL_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>ADL Tracking</a>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(appendRoleQueryParam(RouterAddress.PLATFORM_FEATURES_PAGE, userRoleType)); }} rel="noopener noreferrer" style={footerLinkStyle}>Platform Features</a>
            </div>

            <div className="col flex flex-column align-items-center mb-4">
              <div className="fw-bold mb-2" style={{ color: "#0f3d63" }}><strong>Training</strong></div>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.BOOK_DEMO_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>Book Demo</a>
              {showTutorialVideosLink ? (
                <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(appendRoleQueryParam(RouterAddress.TUTORIALS_PAGE, userRoleType)); }} rel="noopener noreferrer" style={footerLinkStyle}>Tutorial Videos</a>
              ) : null}
            </div>

            <div className="col flex flex-column align-items-center mb-4">
              <div className="fw-bold mb-2" style={{ color: "#0f3d63" }}><strong>HIPAA &amp; Legal</strong></div>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.HIPAA_COMPLIANCE_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>HIPAA and Security</a>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.BUSINESS_ASSOCIATE_AGREEMENT_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>Business Associate Agreement</a>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.PRIVACY_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>Privacy Policy</a>
              <a onClick={(e) => { e.preventDefault(); navigateToPublicPage(RouterAddress.TERMS_OF_SERVICE_PAGE); }} rel="noopener noreferrer" style={footerLinkStyle}>Terms of Service</a>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header={renderSurfaceHeader("Contact Us", authExperience.shell.contactDialogSubtitle, "pi pi-envelope")}
        visible={displayContactDialog}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setDisplayContactDialog(false)}
      >
        <div style={appFormSectionStyle}>
          <ContactCard />
        </div>
      </Dialog>
    </footer>
  );
};

export default Footer;
